import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { getConnectionStatus } from './scribeConnection';

const unavailable = 'Could not load this client’s MindVault contact details. Retry before viewing packages.';

async function contactValue(value) {
  if (value == null || value === '') return '';
  if (typeof value !== 'string') throw new Error('Invalid contact');
  if (!value.startsWith('\\x')) return value.trim();
  const { data, error } = await supabase.rpc('decrypt_text', { encrypted_data: value });
  if (error || typeof data !== 'string') throw new Error('Contact decryption failed');
  return data.trim();
}

// Read-only overlay: never overwrite the Scribe patient or historical records.
export default function useLinkedClientContact({ patient, therapistUserId, connectionVersion, viewKey }) {
  const [revision, setRevision] = useState(0);
  const [snapshot, setSnapshot] = useState({ key: null });
  const email = String(patient?.email || '').trim().toLowerCase();
  const key = JSON.stringify([patient?.id, email, therapistUserId, connectionVersion, viewKey, revision]);
  const refresh = useCallback(() => setRevision(value => value + 1), []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (!email) {
          if (active) setSnapshot({ key, linked: false });
          return;
        }
        if (!therapistUserId) throw new Error('Sign in required');
        const status = await getConnectionStatus(therapistUserId, email, { throwOnError: true });
        if (!active) return;
        if (!status.connected) {
          // A stale "linked" workspace must not quietly fall back to its old
          // contact when permission changes. Reopening rechecks the connection.
          if (connectionVersion === true) throw new Error('Connection changed');
          setSnapshot({ key, linked: false });
          return;
        }
        if (!status.mindvaultAccountId) throw new Error('Linked account unavailable');
        const { data, error } = await supabase.from('mindvault_accounts')
          .select('id,email,phone_code,phone_number').eq('id', status.mindvaultAccountId).maybeSingle();
        if (error || !data || data.id !== status.mindvaultAccountId) throw new Error('Linked contact unavailable');
        const [resolvedEmail, phoneCode, phoneNumber] = await Promise.all([
          contactValue(data.email), contactValue(data.phone_code), contactValue(data.phone_number),
        ]);
        if (resolvedEmail.toLowerCase() !== email) throw new Error('Linked contact mismatch');
        if (active) setSnapshot({ key, linked: true,
          contact: { email: resolvedEmail, phone_code: phoneCode, phoneNumber } });
      } catch (_) {
        if (active) setSnapshot({ key, error: unavailable });
      }
    };
    load();
    return () => { active = false; };
    // key includes the actor, patient, connection status and explicit refresh.
  }, [key, email, therapistUserId, connectionVersion]);

  const loading = snapshot.key !== key;
  const error = loading ? '' : snapshot.error || '';
  const linked = !loading && Boolean(snapshot.linked);
  const readOnly = loading || Boolean(error) || linked;
  const contact = loading || error
    ? { email: '', phone_code: '', phoneNumber: '' }
    : snapshot.contact;
  return { loading, error, linked, readOnly, refresh,
    patient: readOnly ? { ...patient, ...contact } : patient };
}
